import { defineStore } from "pinia";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import { orderFormRestClient } from "@/api";
import { useAnalysisResultStore } from "@/store/modules/analysisResult";
import { useProjectsStore } from "@/store/modules/projects";

const { innerCreate, innerFetch } = crudGenerator(db.models.OrderForm);

export const useOrderFormsStore = defineStore("orderForms", {
  state: () => ({
    orderFormList: [],
    totalOrderForms: 0,
    orderFormIsLoading: false,
  }),
  actions: {
    async fetchOrderForms(projectUuid) {
      return innerFetch(
        { project: projectUuid },
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.orderFormIsLoading = isLoading;
        }
      );
    },

    async createOrderForm(form) {
      return innerCreate(form, useProjectsStore().isOfflineState);
    },

    async setOrderForm(projectUuid) {
      if (!projectUuid) return;
      this.orderFormIsLoading = true;
      const orderForms = await db.orderForms
        .where({ project: projectUuid })
        .toArray();
      this.orderFormList = orderForms;
      this.totalOrderForms += orderForms.length;
      this.orderFormIsLoading = false;
    },

    async reimportResults(documentUuid) {
      const projectUuid = useProjectsStore().currentProjectUuid;
      const orderForm = await orderFormRestClient.reimportResults(
        projectUuid,
        documentUuid
      );
      await db.models.OrderForm.createOrUpdate(orderForm);
      await this.purgeReimportedAnalysisResults(orderForm, projectUuid);

      const orderFormInstance = await db.orderForms.get({
        uuid: orderForm.uuid,
      });
      await orderFormInstance.setRelationships();
      this.upsertOrderForm(orderFormInstance);
      return orderForm;
    },

    async purgeReimportedAnalysisResults(orderForm, projectUuid) {
      const localUuids = await db.analysisResult
        .where("orderForm")
        .equals(orderForm.uuid)
        .primaryKeys();
      const refreshed =
        await useAnalysisResultStore().fetchAnalysisResult(projectUuid);
      if (!refreshed) return;

      const keptUuids = new Set(refreshed.map(({ uuid }) => uuid));
      await db.analysisResult.bulkDelete(
        localUuids.filter((uuid) => !keptUuids.has(uuid))
      );
    },

    upsertOrderForm(orderForm) {
      const index = this.orderFormList.findIndex(
        ({ uuid }) => orderForm.uuid === uuid
      );

      if (index < 0) {
        this.orderFormList.push(orderForm);
      } else {
        this.orderFormList[index] = orderForm;
        this.orderFormList = [...this.orderFormList];
      }
    },
  },
});
